import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from '../lib/uuid';
import api from '../lib/api';

const LEGACY_KEY = 'asset-tracker-state-v7';
const MIGRATED_KEY = 'asset-tracker-migrated-v1';

const AppContext = createContext(null);

const emptyState = {
  user: null,
  loading: true,
  authReady: false,
  authenticated: false,

  // Workspace
  dropdowns: {},
  sources: [],
  snapshots: [],
  holdings: {},
  holdingsMeta: {},
  assets: [],
  backups: {},
  locks: {},
  fxRates: {},
  dashboardViews: [],
  isAdmin: false,
  sharedSnapshotIds: [],
};

export const AppProvider = ({ children }) => {
  const [state, setState] = useState(emptyState);

  const refreshAll = useCallback(async () => {
    const data = await api.bootstrap();
    setState((s) => ({
      ...s,
      user: data.user,
      authenticated: true,
      authReady: true,
      loading: false,
      dropdowns: data.dropdowns || {},
      sources: data.sources || [],
      snapshots: (data.snapshots || []).slice().sort((a, b) => a.date.localeCompare(b.date)),
      assets: data.assets || [],
      holdings: data.holdings || {},
      holdingsMeta: data.holdingsMeta || {},
      locks: data.locks || {},
      backups: data.backups || {},
      fxRates: data.fxRates || {},
      dashboardViews: data.dashboardViews || [],
      isAdmin: !!data.isAdmin,
      sharedSnapshotIds: data.sharedSnapshotIds || [],
    }));
  }, []);

  // Initial auth check + workspace load
  useEffect(() => {
    let cancelled = false;
    const init = async () => {
      // CRITICAL: skip /me if returning from OAuth callback — AuthCallback will exchange first.
      if (window.location.hash?.includes('session_id=')) {
        setState((s) => ({ ...s, loading: false, authReady: true }));
        return;
      }
      try {
        await api.me();
        if (cancelled) return;
        await refreshAll();
      } catch (e) {
        if (cancelled) return;
        setState((s) => ({ ...s, authenticated: false, authReady: true, loading: false }));
      }
    };
    init();
    return () => { cancelled = true; };
  }, [refreshAll]);

  // ----- Sources -----
  const addSource = async (src) => {
    const created = await api.createSource(src);
    setState((s) => ({ ...s, sources: [...s.sources, created] }));
  };
  const updateSource = async (id, patch) => {
    const updated = await api.updateSource(id, patch);
    setState((s) => ({ ...s, sources: s.sources.map((x) => x.id === id ? updated : x) }));
  };
  const deleteSource = async (id) => {
    await api.deleteSource(id);
    setState((s) => ({ ...s, sources: s.sources.filter((x) => x.id !== id) }));
  };

  // ----- Dropdowns -----
  const addDropdownValue = async (key, val) => {
    const v = (val || '').trim();
    if (!v) return;
    if ((state.dropdowns[key] || []).includes(v)) return;
    await api.addDropdownValue(key, v);
    setState((s) => ({ ...s, dropdowns: { ...s.dropdowns, [key]: [...(s.dropdowns[key] || []), v] } }));
  };
  const removeDropdownValue = async (key, val) => {
    await api.removeDropdownValue(key, val);
    setState((s) => ({ ...s, dropdowns: { ...s.dropdowns, [key]: (s.dropdowns[key] || []).filter((v) => v !== val) } }));
  };
  const updateDropdownValue = async (key, oldVal, newVal) => {
    await api.renameDropdownValue(key, oldVal, newVal);
    setState((s) => ({
      ...s,
      dropdowns: { ...s.dropdowns, [key]: (s.dropdowns[key] || []).map((v) => v === oldVal ? newVal : v) },
    }));
  };

  // ----- Snapshots -----
  const addSnapshot = async (date, label) => {
    try {
      const created = await api.createSnapshot(date, label);
      setState((s) => ({
        ...s,
        snapshots: [...s.snapshots, created].sort((a, b) => a.date.localeCompare(b.date)),
        holdings: { ...s.holdings, [created.id]: Object.fromEntries(s.sources.map((src) => [src.id, []])) },
      }));
      return created.id;
    } catch (e) {
      if (e.status === 409) {
        // Already exists — refresh to make sure local state matches server
        await refreshAll();
        return `snap-${date}`;
      }
      throw e;
    }
  };

  const deleteSnapshot = async (snapId) => {
    const result = await api.deleteSnapshot(snapId);
    // Refresh full state — easiest way to ensure dashboards/views/locks/backups all reflect the cascade
    await refreshAll();
    return result;
  };

  const updateSnapshot = async (snapId, patch) => {
    const updated = await api.updateSnapshot(snapId, patch);
    setState((s) => ({
      ...s,
      snapshots: s.snapshots
        .map((x) => (x.id === snapId ? updated : x))
        .sort((a, b) => a.date.localeCompare(b.date)),
    }));
    return updated;
  };

  // ----- Assets -----
  const findAssetByName = (name) => {
    const norm = (name || '').trim().toLowerCase();
    return state.assets.find((a) => a.investmentName.toLowerCase() === norm) || null;
  };

  const addAsset = async (body) => {
    const created = await api.createAsset(body);
    setState((s) => {
      if (s.assets.find((a) => a.id === created.id)) return s;
      return { ...s, assets: [...s.assets, created] };
    });
    return created;
  };
  const updateAsset = async (assetId, patch) => {
    const updated = await api.updateAsset(assetId, patch);
    setState((s) => ({ ...s, assets: s.assets.map((a) => a.id === assetId ? updated : a) }));
  };
  const deleteAsset = async (assetId) => {
    await api.deleteAsset(assetId);
    setState((s) => ({ ...s, assets: s.assets.filter((a) => a.id !== assetId) }));
  };

  // ----- Holdings -----
  const saveHoldings = async (snapshotId, sourceId, rows) => {
    const result = await api.saveHoldings(snapshotId, sourceId, rows);
    // After holdings save the backend may have inserted new assets — refresh assets + sources + locks
    const data = await api.bootstrap();
    setState((s) => ({
      ...s,
      holdings: data.holdings || {},
      holdingsMeta: data.holdingsMeta || {},
      assets: data.assets || [],
      sources: data.sources || [],
      locks: data.locks || {},
    }));
    return result;
  };

  const getHoldings = (snapshotId, sourceId) => {
    const snap = state.holdings[snapshotId];
    if (!snap) return [];
    return snap[sourceId] || [];
  };

  // ----- Locks -----
  const isPeriodLocked = (snapId) => !!state.locks[snapId]?.locked;
  const isSourceComplete = (snapId, srcId) => !!state.locks[snapId]?.sources?.[srcId]?.complete;

  const setSourceComplete = async (snapId, srcId, complete) => {
    await api.setSourceComplete(snapId, srcId, complete);
    setState((s) => {
      const curr = s.locks[snapId] || { locked: false, sources: {} };
      return {
        ...s,
        locks: { ...s.locks, [snapId]: { ...curr, sources: { ...(curr.sources || {}), [srcId]: { complete } } } },
      };
    });
  };

  const setPeriodLocked = async (snapId, locked) => {
    await api.setPeriodLock(snapId, locked);
    setState((s) => {
      const curr = s.locks[snapId] || { locked: false, sources: {} };
      return { ...s, locks: { ...s.locks, [snapId]: { ...curr, locked } } };
    });
  };

  // ----- Backups -----
  const addBackup = async (snapId, srcId, backup) => {
    await api.addBackup(snapId, srcId, backup);
    setState((s) => {
      const forSnap = s.backups[snapId] || {};
      const list = forSnap[srcId] || [];
      return { ...s, backups: { ...s.backups, [snapId]: { ...forSnap, [srcId]: [...list, backup] } } };
    });
  };
  const removeBackup = async (snapId, srcId, backupId) => {
    await api.removeBackup(snapId, srcId, backupId);
    setState((s) => {
      const forSnap = s.backups[snapId] || {};
      const list = (forSnap[srcId] || []).filter((b) => b.id !== backupId);
      return { ...s, backups: { ...s.backups, [snapId]: { ...forSnap, [srcId]: list } } };
    });
  };
  const getBackups = (snapId, srcId) => state.backups[snapId]?.[srcId] || [];

  // ----- FX rates (manual only, per-source + snapshot default) -----
  // Shape: state.fxRates = { [snapId]: { [srcId]: {...}, _default: {...} } }
  //
  // getFxRate(snap) → snapshot-default doc (or null)
  // getFxRate(snap, src) → source-specific doc (or null — callers should fall back)
  // getFxRate(snap, src, { resolve: true }) → first available per lookup order
  const getFxRate = (snapId, srcId = null, opts = {}) => {
    const bySnap = state.fxRates[snapId];
    if (!bySnap) return null;
    if (srcId) {
      if (bySnap[srcId]) return bySnap[srcId];
      if (!opts.resolve) return null;
      // Resolve: snapshot default, else any per-source entry in this snapshot
      if (bySnap._default) return bySnap._default;
      const fallback = Object.entries(bySnap)
        .find(([k, v]) => k !== '_default' && v && typeof v === 'object')?.[1];
      return fallback || null;
    }
    return bySnap._default || null;
  };

  const setFxRate = async (snapId, srcIdOrPayload, maybePayload, opts = {}) => {
    // Backward-compatible signature:
    //   setFxRate(snap, payload)              → snapshot default (srcId=null)
    //   setFxRate(snap, srcId, payload)       → per-source rate
    //   setFxRate(snap, srcId, payload, opts) → per-source + options
    let srcId = null;
    let payload = srcIdOrPayload;
    let options = opts;
    if (typeof srcIdOrPayload === 'string' || srcIdOrPayload === null) {
      srcId = srcIdOrPayload;
      payload = maybePayload;
    } else if (maybePayload && typeof maybePayload === 'object' && !Array.isArray(maybePayload)) {
      // Shouldn't happen, but treat as options
      options = maybePayload;
    }

    await api.setFxRate(snapId, srcId, payload, options.source || 'manual');

    setState((s) => {
      const bySnap = s.fxRates[snapId] || {};
      const key = srcId || '_default';
      const prev = bySnap[key] || {};
      let nextCost = prev.usdInrCost || 0;
      let nextMv = prev.usdInrMv || 0;
      if (typeof payload === 'number') {
        nextCost = nextMv = payload;
      } else if (payload && typeof payload === 'object') {
        if (payload.usdInrCost != null) nextCost = Number(payload.usdInrCost);
        if (payload.usdInrMv   != null) nextMv   = Number(payload.usdInrMv);
        if (payload.usdInr     != null) {
          if (payload.usdInrCost == null) nextCost = Number(payload.usdInr);
          if (payload.usdInrMv   == null) nextMv   = Number(payload.usdInr);
        }
      }
      return {
        ...s,
        fxRates: {
          ...s.fxRates,
          [snapId]: {
            ...bySnap,
            [key]: {
              usdInr: nextCost || nextMv,
              usdInrCost: nextCost,
              usdInrMv: nextMv,
              fetchedAt: new Date().toISOString(),
              manual: true,
              source: options.source || 'manual',
            },
          },
        },
      };
    });
  };

  const clearSourceFxRate = async (snapId, srcId) => {
    await api.clearSourceFxRate(snapId, srcId);
    setState((s) => {
      const bySnap = { ...(s.fxRates[snapId] || {}) };
      delete bySnap[srcId];
      return { ...s, fxRates: { ...s.fxRates, [snapId]: bySnap } };
    });
  };

  // Suggest prior-period rates — per-source first, then snapshot default fallback.
  const fallbackFxFromPrior = (snapId, srcId = null) => {
    const snap = state.snapshots.find((x) => x.id === snapId);
    if (!snap) return null;
    const prior = [...state.snapshots]
      .filter((s) => s.date < snap.date)
      .sort((a, b) => b.date.localeCompare(a.date));
    for (const p of prior) {
      const bySnap = state.fxRates[p.id];
      if (!bySnap) continue;
      const pick = (srcId && bySnap[srcId]) || bySnap._default ||
                   Object.entries(bySnap).find(([k, v]) => k !== '_default' && v && typeof v === 'object')?.[1];
      if (!pick) continue;
      const cost = pick.usdInrCost || pick.usdInr;
      const mv = pick.usdInrMv || pick.usdInr;
      if (cost || mv) return { cost: cost || mv, mv: mv || cost, from: p };
    }
    return null;
  };

  // Returns true if any holding (across all snapshots) has geography === 'Offshore'
  const hasOffshoreHoldings = (snapId = null) => {
    const checkSnap = (sId) => {
      const bySrc = state.holdings[sId] || {};
      return Object.values(bySrc).some((rows) => (rows || []).some((r) => r.geography === 'Offshore'));
    };
    if (snapId) return checkSnap(snapId);
    return state.snapshots.some((s) => checkSnap(s.id));
  };

  // ----- Dashboard Views -----
  const addDashboardView = async (view) => {
    const created = await api.createView(view);
    setState((s) => ({ ...s, dashboardViews: [...(s.dashboardViews || []), created] }));
    return created.id;
  };
  const updateDashboardView = async (id, patch) => {
    const updated = await api.updateView(id, patch);
    setState((s) => ({ ...s, dashboardViews: (s.dashboardViews || []).map((v) => v.id === id ? updated : v) }));
  };
  const deleteDashboardView = async (id) => {
    await api.deleteView(id);
    setState((s) => ({ ...s, dashboardViews: (s.dashboardViews || []).filter((v) => v.id !== id) }));
  };

  // ----- Reset / migrate -----
  const resetAll = async () => {
    await api.resetAll();
    await refreshAll();
  };

  // Detect legacy localStorage state (used by MigrationPrompt)
  const getLegacyState = () => {
    try {
      const raw = localStorage.getItem(LEGACY_KEY);
      if (!raw) return null;
      if (localStorage.getItem(MIGRATED_KEY) === '1') return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (_) { return null; }
  };

  const importLegacyState = async (legacy) => {
    await api.migrate({ ...legacy, replace: true });
    localStorage.setItem(MIGRATED_KEY, '1');
    await refreshAll();
  };

  const dismissLegacyState = () => {
    localStorage.setItem(MIGRATED_KEY, '1');
  };

  // ----- Auth -----
  const logout = async () => {
    await api.logout();
    setState({ ...emptyState, loading: false, authReady: true, authenticated: false });
  };

  const value = useMemo(() => ({
    ...state,
    currency: 'INR',
    refreshAll,
    addSource, updateSource, deleteSource,
    addDropdownValue, removeDropdownValue, updateDropdownValue,
    addSnapshot, deleteSnapshot, updateSnapshot, saveHoldings, getHoldings,
    findAssetByName, addAsset, updateAsset, deleteAsset,
    isPeriodLocked, isSourceComplete, setSourceComplete, setPeriodLocked,
    addBackup, removeBackup, getBackups,
    getFxRate, setFxRate, clearSourceFxRate, fallbackFxFromPrior, hasOffshoreHoldings,
    addDashboardView, updateDashboardView, deleteDashboardView,
    resetAll, logout,
    getLegacyState, importLegacyState, dismissLegacyState,
    // Helper: ID generator for client-side row ids before saving
    nextRowId: () => uuid(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [state, refreshAll]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be used within AppProvider');
  return ctx;
};
